import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, Mic, MicOff, Phone, PhoneOff } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent } from "../ui/dialog";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import { ensureWavoip, formatarParaE164 } from "../../services/wavoipClient";
import { startRingtone, stopRingtone } from "../../services/ringtone";

const ESTADOS = {
  conectando: "Conectando…",
  chamando: "Chamando…",
  falando: "Em conversa",
  encerrada: "Chamada encerrada",
  cancelada: "Cancelada por você",
  recusada: "Recusada",
  nao_atendida: "Não atendida",
  erro: "Falha ao ligar"
};

const ESTADOS_FINAIS = new Set(["encerrada", "cancelada", "recusada", "nao_atendida", "erro"]);

const formatarDuracao = (segundos) => {
  const m = String(Math.floor(segundos / 60)).padStart(2, "0");
  const s = String(segundos % 60).padStart(2, "0");
  return `${m}:${s}`;
};

const CallButton = ({ ticket }) => {
  const [token, setToken] = useState(null);
  const [verificando, setVerificando] = useState(true);
  const [aberto, setAberto] = useState(false);
  const [estado, setEstado] = useState("");
  const [mudo, setMudo] = useState(false);
  const [duracao, setDuracao] = useState(0);

  const saidaRef = useRef(null);
  const ativaRef = useRef(null);
  const cronometro = useRef(null);
  const timeoutProgressoRef = useRef(null);
  const unsubsRef = useRef([]);

  const numeroCru = String(ticket?.contact?.number || "").replace(/\D/g, "");
  const numeroE164 = formatarParaE164(numeroCru);
  const podeLigar = Boolean(token) && numeroCru.length >= 10 && !ticket?.isGroup;

  const limparTimeoutProgresso = () => {
    if (timeoutProgressoRef.current) {
      clearTimeout(timeoutProgressoRef.current);
      timeoutProgressoRef.current = null;
    }
  };

  const pararCronometro = () => {
    if (cronometro.current) {
      clearInterval(cronometro.current);
      cronometro.current = null;
    }
  };

  const iniciarCronometro = () => {
    pararCronometro();
    setDuracao(0);
    cronometro.current = setInterval(
      () => setDuracao((d) => d + 1),
      1000
    );
  };

  const unsubscribeTudo = () => {
    while (unsubsRef.current.length) {
      const fn = unsubsRef.current.pop();
      try { fn(); } catch (_e) {}
    }
  };

  const adicionarUnsub = (fn) => {
    if (typeof fn === "function") unsubsRef.current.push(fn);
  };

  const verificar = useCallback(async () => {
    if (!ticket?.whatsappId) {
      setVerificando(false);
      return;
    }
    try {
      const endpoint = `/wavoip/${ticket.whatsappId}`.replace(/\/+/g, "/");
      const { data, headers } = await api.get(endpoint, {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
          "Cache-Control": "no-cache, no-store",
          Pragma: "no-cache"
        },
      });
      const ct = String(headers?.["content-type"] || "");
      const cru = JSON.stringify(data || {});
      if (/text\/html/i.test(ct) || /^<!doctype/i.test(cru.slice(0, 200).toLowerCase())) {
        toast.error(
          "Wavoip: service worker retornou página em vez de JSON. Limpe 'Clear site data' em DevTools → Application → Storage."
        );
        setToken(null);
        setVerificando(false);
        return;
      }
      if (!data?.token) {
        // conexão sem token, só avisar em desenvolvimento/primeira vez para
        // não ficar bombardeando o usuário em todo ticket.
      }
      setToken(data?.token || null);
    } catch (err) {
      setToken(null);
      try {
        const status = err?.response?.status;
        const ct = String(err?.response?.headers?.["content-type"] || "");
        const tipo = String(err?.response?.data || "");
        const wasHtml = /text\/html/i.test(ct) || /<!doctype/i.test(String(tipo).slice(0, 200).toLowerCase());
        if (status === 401 || status === 403) {
          toast.error("Sem permissão para ler token Wavoip (401/403).");
        } else if (wasHtml) {
          toast.error(
            "Wavoip: service worker retornou página em vez de JSON. Limpe 'Clear site data'."
          );
        } else if (status && status >= 400) {
          toast.error(`Wavoip falhou ao buscar token (HTTP ${status}).`);
        } else if (typeof window !== "undefined" && !window.navigator?.onLine) {
          toast.warn("Wavoip: sem conexão de rede.");
        }
      } catch (_e) {}
    }
    setVerificando(false);
  }, [ticket?.whatsappId]);

  useEffect(() => { verificar(); }, [verificar]);

  // Libera tudo ao desmontar (fechar ticket, trocar de tela).
  const desligar = useCallback(() => {
    pararCronometro();
    limparTimeoutProgresso();
    unsubscribeTudo();

    const ativa = ativaRef.current;
    ativaRef.current = null;
    if (ativa) {
      try { ativa.end?.(); } catch (_e) {}
      try { ativa.cancel?.(); } catch (_e) {}
    }
    const saida = saidaRef.current;
    saidaRef.current = null;
    if (saida) {
      try { saida.cancel?.(); } catch (_e) {}
      try { saida.end?.(); } catch (_e) {}
    }
  }, []);

  useEffect(() => desligar, [desligar]);

  // Ringtone (chamando) durante o "Chamando…" até o peer atender ou falhar.
  useEffect(() => {
    if (estado === "chamando") {
      try { startRingtone(); } catch (_e) {}
      return () => stopRingtone();
    }
    stopRingtone();
    return undefined;
  }, [estado]);

  const fecharModalLimpo = useCallback(() => {
    desligar();
    setEstado("");
    setDuracao(0);
    setMudo(false);
    setAberto(false);
  }, [desligar]);

  const encerrar = useCallback(() => {
    const estadoAtual = estado;
    desligar();
    setDuracao(0);
    setMudo(false);
    if (!ESTADOS_FINAIS.has(estadoAtual)) {
      setEstado("cancelada");
    }
  }, [desligar, estado]);

  const ligar = async () => {
    if (!podeLigar) {
      toast.info("Este contato não tem telefone para receber ligação.");
      return;
    }
    setAberto(true);
    setEstado("conectando");
    setDuracao(0);
    setMudo(false);
    desligar();

    try {
      const wav = await ensureWavoip([token]);
      setEstado("chamando");
      limparTimeoutProgresso();
      timeoutProgressoRef.current = setTimeout(() => {
        try {
          if (estado !== "falando" && estado !== "encerrada") {
            toast.warn(
              "Sem progresso da Wavoip após 8s. Verifique token, conexão ou recarregue."
            );
          }
        } catch (_e) {}
      }, 8000);

      // fromTokens força usar a conexão deste ticket (a que o usuário acabou
      // de colar o token), não qualquer outra conexão da empresa que tenha
      // token ativo. Evita ligar de número errado.
      const { call, err } = await wav.startCall({
        to: numeroE164,
        fromTokens: [token]
      });

      if (err || !call) {
        limparTimeoutProgresso();
        const razoes =
          err?.devices
            ?.map?.((d) => `${d.token?.slice(0, 8)}… → ${d.reason}`)
            .join("; ") || "";
        toast.error(
          `Wavoip recusou a chamada: ${err?.message || "sem detalhes"}${razoes ? ` — ${razoes}` : ""}`
        );
        setMudo(false);
        setDuracao(0);
        setEstado("erro");
        return;
      }

      saidaRef.current = call;

      adicionarUnsub(call.on("peerAccept", (ativa) => {
        try {
          limparTimeoutProgresso();
          ativaRef.current = ativa;
          setEstado("falando");
          iniciarCronometro();
          setMudo(false);

          adicionarUnsub(ativa.on("ended", () => {
            limparTimeoutProgresso();
            pararCronometro();
            if (!ESTADOS_FINAIS.has(estado)) setEstado("encerrada");
          }));
          adicionarUnsub(ativa.on("peerMute", () => setMudo(true)));
          adicionarUnsub(ativa.on("peerUnmute", () => setMudo(false)));
          adicionarUnsub(ativa.on("error", (reason) => {
            toast.error(`Falha na ligação: ${String(reason || "erro transporte")}`);
            limparTimeoutProgresso();
            pararCronometro();
            setEstado("erro");
          }));
          adicionarUnsub(ativa.on("connectionStatus", (s) => {
            if (s === "reconnecting") toast.warn("Mídia reconectando…");
          }));
        } catch (_e) {}
      }));

      adicionarUnsub(call.on("peerReject", () => {
        try {
          limparTimeoutProgresso();
          pararCronometro();
          setEstado("recusada");
        } catch (_e) {}
      }));

      adicionarUnsub(call.on("unanswered", () => {
        try {
          limparTimeoutProgresso();
          pararCronometro();
          setEstado("nao_atendida");
        } catch (_e) {}
      }));

      adicionarUnsub(call.on("ended", () => {
        try {
          limparTimeoutProgresso();
          pararCronometro();
          if (!ESTADOS_FINAIS.has(estado)) setEstado("encerrada");
        } catch (_e) {}
      }));

      adicionarUnsub(call.on("status", (s) => {
        try {
          if (s === "CALLING" || s === "RINGING") {
            if (estado !== "falando") setEstado("chamando");
            return;
          }
          if (s === "ACTIVE") setEstado("falando");
          if (s === "NOT_ANSWERED") { limparTimeoutProgresso(); pararCronometro(); setEstado("nao_atendida"); }
          else if (s === "REJECTED") { limparTimeoutProgresso(); pararCronometro(); setEstado("recusada"); }
          else if (s === "CANCELLED") { limparTimeoutProgresso(); pararCronometro(); setEstado("cancelada"); }
          else if (s === "FAILED" || s === "DISCONNECTED") { limparTimeoutProgresso(); pararCronometro(); setEstado("erro"); }
          else if (s === "ENDED") { limparTimeoutProgresso(); pararCronometro(); if (!ESTADOS_FINAIS.has(estado)) setEstado("encerrada"); }
        } catch (_e) {}
      }));
    } catch (err) {
      toast.error(`Não foi possível iniciar a ligação: ${err?.message || "erro desconhecido"}`);
      setMudo(false);
      setDuracao(0);
      setEstado("erro");
    }
  };

  const alternarMudo = async () => {
    const alvo = ativaRef.current || saidaRef.current;
    if (!alvo) return;
    try {
      let r;
      if (mudo) r = await alvo.unmute(); else r = await alvo.mute();
      if (r?.err) throw new Error(r.err);
      setMudo((m) => !m);
    } catch (err) {
      toast.error(`Não foi possível mudar o microfone: ${err?.message || "erro"}`);
    }
  };

  if (verificando || !token) return null;

  const ehEstadoFinal = ESTADOS_FINAIS.has(estado);
  const iconeCirculoCor = cn(
    "flex h-16 w-16 items-center justify-center rounded-full",
    estado === "falando" && "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
    estado === "chamando" && "bg-sky-500/10 text-sky-600",
    estado === "conectando" && "bg-muted text-muted-foreground",
    estado === "encerrada" && "bg-slate-500/10 text-slate-600",
    estado === "cancelada" && "bg-amber-500/10 text-amber-600",
    estado === "recusada" && "bg-rose-500/10 text-rose-600",
    estado === "nao_atendida" && "bg-orange-500/10 text-orange-600",
    estado === "erro" && "bg-red-500/10 text-red-600",
  );

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-9 w-9"
        aria-label="Ligar para o contato"
        title="Ligar"
        onClick={ligar}
      >
        <Phone className="h-4 w-4" />
      </Button>

      <Dialog
        open={aberto}
        onOpenChange={(v) => {
          if (!v) fecharModalLimpo();
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <div className="flex flex-col items-center gap-4 py-2">
            <span className={iconeCirculoCor}>
              {estado === "conectando" ? (
                <Loader2 className="h-7 w-7 animate-spin" />
              ) : (
                <Phone
                  className={cn(
                    "h-7 w-7",
                    estado === "chamando" && "animate-pulse"
                  )}
                />
              )}
            </span>

            <div className="text-center">
              <p className="text-base font-semibold">{ticket?.contact?.name || "Contato"}</p>
              <p className="text-xs tabular-nums text-muted-foreground">
                Para: <span className="text-foreground font-medium">{numeroE164 || "—"}</span>
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                {ESTADOS[estado] || "Ligação"}
              </p>
              {estado === "falando" && (
                <p className="text-sm tabular-nums text-muted-foreground">
                  {formatarDuracao(duracao)}
                </p>
              )}
              {ehEstadoFinal && duracao > 0 && estado !== "encerrada" && (
                <p className="text-xs tabular-nums text-muted-foreground">
                  Duração: {formatarDuracao(duracao)}
                </p>
              )}
            </div>

            {ehEstadoFinal ? (
              <div className="flex w-full gap-2">
                <Button
                  variant="secondary"
                  className="flex-1"
                  onClick={fecharModalLimpo}
                >
                  Fechar
                </Button>
              </div>
            ) : (
              <div className="flex w-full gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={alternarMudo}
                  disabled={estado !== "falando"}
                >
                  {mudo ? (
                    <MicOff className="h-4 w-4" />
                  ) : (
                    <Mic className="h-4 w-4" />
                  )}
                  {mudo ? "Reativar" : "Mudo"}
                </Button>

                <Button
                  variant="destructive"
                  className="flex-1"
                  onClick={encerrar}
                >
                  <PhoneOff className="h-4 w-4" />
                  {estado === "falando" ? "Encerrar" : "Cancelar"}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CallButton;
